import React, { ChangeEvent, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Trans, useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Checkbox, TextField } from '@mui/material';
import { OrganizationProductsFetcher } from '../Customer/CustomerFetchers';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import dayjs, { Dayjs } from 'dayjs';
import { apiOrganizationProduct } from '../../resources/api/apiOrganizationProduct';
import { useFormattedNow } from '../../hooks/useFormattedNow';
import { dateFormat, DateFormat } from '../../helpers/dateFormat';
import { IOrganizationProduct } from '../../resources/models/IOrganizationProduct';
import { TogglableDateFormPartial } from '../TogglableDateFormPartial/TogglableDateFormPartial';
import { CustomerProductLicensesFetcher } from '../../fetchers/CustomerProductLicensesFetcher';
import { CustomerProductPlanType } from '../../models/CustomerProductPlanType';

interface IProps {
  open: boolean;
  closeDialog: () => void;
  customerProduct: IOrganizationProduct;
}

const { useRenewOrganizationProduct } = apiOrganizationProduct;

export const CustomerProductRenew: FC<IProps> = ({ open, closeDialog, customerProduct }) => {
  const { organization } = useContext(OrganizationContext);
  const { onRefresh } = useContext(OrganizationProductsFetcher.Context);
  const { onRefresh: onRefreshLicenses } = useContext(CustomerProductLicensesFetcher.Context);
  const handleError = useApiErrorHandler();

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [retro, setRetro] = useState<Dayjs | null>(null);
  const [scheduled, setScheduled] = useState<Dayjs | null>(null);
  const [disableNotification, setDisableNotification] = useState(false);
  const [dealId, setDealId] = useState<string>('');

  const { execute: renewCustomerProduct } = useRenewOrganizationProduct(organization ?? void 0, customerProduct.id);

  const prevIsRetro = useRef(false);
  const prevIsScheduled = useRef(false);

  useEffect(() => {
    if (retro && scheduled) {
      if (prevIsRetro.current) {
        setRetro(null);
      } else {
        setScheduled(null);
      }
    }
    prevIsRetro.current = !!retro;
    prevIsScheduled.current = !!scheduled;
  }, [!scheduled, !retro]);

  const renew = useCallback(async () => {
    try {
      const time = retro ?? scheduled ?? void 0;
      const scheduleTime = time ? Math.round(time.valueOf() / 1000) : void 0;

      await renewCustomerProduct({
        sendNotifications: !disableNotification,
        scheduleTime,
        dealId: dealId || undefined,
      });
      await Promise.all([onRefresh(), onRefreshLicenses()]);
      closeDialog();
    } catch (e) {
      handleError(e);
    }
  }, [renewCustomerProduct, retro, scheduled, disableNotification, dealId]);

  const formattedNow = useFormattedNow(DateFormat.DateFullWithTimeAndZone);
  const now = useMemo(() => dayjs().second(0), [formattedNow]);

  // check for product plan type and update the date accordingly
  const currentExpiry = dayjs(
    (customerProduct.planType === CustomerProductPlanType.Subscription
      ? customerProduct.expiresAt
      : customerProduct.updatesExpiresAt) * 1000
  );

  const renewFrom = retro ?? scheduled ?? (currentExpiry.isAfter(now) ? currentExpiry : now);

  const formattedRenewFrom = dateFormat(renewFrom, DateFormat.DateFullWithTimeAndZone, language);

  const formattedEnd = useMemo(() => {
    if (!customerProduct.planDuration) return null;
    const { planDuration } = customerProduct;
    return dateFormat(
      renewFrom.add(Math.floor(planDuration), 'days').add(24 * (planDuration % 1), 'hours'),
      DateFormat.DateFullWithTimeAndZone,
      language
    );
  }, [renewFrom, customerProduct.planDuration]);

  const onDisableNotificationChange = useCallback((_: ChangeEvent, value: boolean) => {
    setDisableNotification(value);
  }, []);

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>{t('customerProduct.renewDialog.title')}</DialogTitle>
      <DialogContent>
        <Trans i18nKey="customerProduct.renewDialog.message">
          <Typography paragraph />
          <Typography paragraph />
        </Trans>
        <Grid container>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={disableNotification} onChange={onDisableNotificationChange} />}
              label={t('customerProduct.renewDialog.disableNotification') as string}
            />
          </Grid>
          <TogglableDateFormPartial
            value={retro}
            onChange={setRetro}
            togglerLabel={t('customerProduct.renewDialog.retroLabel')}
            disableFuture
          />
          <TogglableDateFormPartial
            value={scheduled}
            onChange={setScheduled}
            togglerLabel={t('customerProduct.renewDialog.scheduleLabel')}
            disablePast={currentExpiry.isAfter(now) ? currentExpiry : now}
          />
          <Grid item xs={12}>
            <TextField
              label={t('customerProduct.notificationEditDialog.dealId')}
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} mt={1}>
            {t('customerProduct.renewDialog.startDate')}: {formattedRenewFrom ?? '-'}
          </Grid>
          <Grid item xs={12}>
            {t('customerProduct.renewDialog.endDate')}: {formattedEnd ?? '-'}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('common.dialog.close')}</Button>

        <Button variant="contained" color={scheduled ? 'secondary' : 'primary'} onClick={renew}>
          {scheduled ? t('customerProduct.renewDialog.confirmSchedule') : t('customerProduct.renewDialog.confirmNow')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
